import { RESPONSE_ERRORS } from '@/constants/RESPONSE_ERRORS'

const DEFAULT_FALLBACK = 'Something went wrong. Please try again.'
const NETWORK_ERROR_CODE = 'ERR_NETWORK'

type FastApiDetailItem = {
  msg?: string,
}

const HTTP_SERVER_ERROR_MIN = 500

type AiServiceError = {
  code?: string,
  response?: {
    status?: number,
    data?: {
      detail?: string | FastApiDetailItem[],
      message?: unknown,
    },
  },
}

/**
 * Unwraps a vAI service error body into one display string. ai-parser's
 * HTTPException handler answers `{ message: string }` for nearly every v1 4xx
 * (a dict detail arrives nested: `{ message: { message } }`); FastAPI's own
 * `detail` shows up for pydantic 422 (a list of issues), unmatched routes and
 * the 504 timeout (a string).
 */
export const formatAiServiceError = (
  error: unknown,
  fallback: string = DEFAULT_FALLBACK,
): string => {
  const response = (error as AiServiceError)?.response
  // No response at all: the request never reached vAI, so the action's own text would blame the
  // service for the user's connection.
  if (!response && (error as AiServiceError)?.code === NETWORK_ERROR_CODE) {
    return RESPONSE_ERRORS.network
  }

  // A 5xx body is an internal failure (often a raw exception), never a message for the user.
  if (Number(response?.status) >= HTTP_SERVER_ERROR_MIN) {
    return fallback
  }

  const data = response?.data
  const detail = data?.detail

  if (typeof detail === 'string' && detail.trim()) {
    return detail
  }

  if (Array.isArray(detail)) {
    // `msg` is typed as string, but this is an external service's payload —
    // check the runtime type rather than trusting the annotation, or a
    // non-string msg would make .trim() throw.
    const messages = detail
      .map((item) => item?.msg)
      .filter((message): message is string => {
        return typeof message === 'string' && message.trim().length > 0
      })

    if (messages.length) {
      return messages.join('. ')
    }
  }

  const message = typeof data?.message === 'object' && data.message !== null
    ? (data.message as { message?: unknown }).message
    : data?.message
  if (typeof message === 'string' && message.trim()) {
    return message
  }

  return fallback
}
